import React, { useState, useEffect } from 'react';
import 'grapesjs/dist/css/grapes.min.css';
import grapesjs from 'grapesjs';
import { Button } from 'ui';
import "./index.css";
import { usePage, updatePage, useAuth, loadMedia, uploadCompanyMedia, refreshPagePreview } from 'api';
import { useParams, useHistory } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPaintBrush, faCubes, faLayerGroup, faCog } from '@fortawesome/free-solid-svg-icons';


export default function EditPage(props) {
  
  
  // Load and Save the page
  
  const auth = useAuth();
  const history = useHistory();
  const companyId = auth ? auth.company.id : null;
  const {pageId} = useParams();
  const page = usePage(companyId, pageId);
  
  
  // Setup GrapeJS
  
  const [editor, setEditor] = useState(null);
  useEffect(() => {
    
    if(!page) {
      return;
    }
    
    if(editor) {
      editor.DomComponents.clear();
      editor.CssComposer.clear();
      editor.UndoManager.clear();
      editor.setComponents(page.html);
      editor.setStyle(page.css);
      return;
    }
    
    
    
    // Configure GrapeJS editor
    // https://grapesjs.com/docs/getting-started.html#start-from-the-canvas
    
    const instance = grapesjs.init({
      
      // Inject content.
      fromElement: false,
      
      components: page.html || "<div></div>",
      style: page.css || "",
      
      
      
      // Indicate where to init the editor. You can also pass an HTMLElement
      container: '#gjs',
      
      // Size of the editor
      height: '100%',
      width: '100%',
      
      baseCss: `
        * {
          box-sizing: border-box;
        }
        html, body, #wrapper {
          min-height: 100%;
        }
        body {
          margin: 0;
          height: 100%;
          background-color: #fff
        }
      `,
      
      // We define a default panel as a sidebar to contain layers
      panels: {
        defaults: [
          {
            id: 'pageedit-right',
            el: '#pageedit-right',
            // Make the panel resizable
            resizable: {
              maxDim: 500,
              minDim: 300,
              tc: 0, // Top handler
              cl: 1, // Left handler
              cr: 0, // Right handler
              bc: 0, // Bottom handler
              // Being a flex child we need to change `flex-basis` property
              // instead of the `width` (default)
              // keyWidth: 'flex-basis',
            },
          },
          {
            id: 'pageedit-devices',
            el: '#pageedit-devices',
            buttons: [{
                id: 'device-desktop',
                label: `
                  <svg width="24" height="24" viewBox="0 0 576 512"><path fill="currentColor" d="M528 0H48C21.5 0 0 21.5 0 48v320c0 26.5 21.5 48 48 48h192l-16 48h-72c-13.3 0-24 10.7-24 24s10.7 24 24 24h272c13.3 0 24-10.7 24-24s-10.7-24-24-24h-72l-16-48h192c26.5 0 48-21.5 48-48V48c0-26.5-21.5-48-48-48zm-16 352H64V64h448v288z"></path></svg>
                `,
                command: 'set-device-desktop',
                active: true,
                togglable: false,
              }, {
                id: 'device-mobile',
                label: `
                  <svg width="24" height="24" viewBox="0 0 320 512"><path fill="currentColor" d="M272 0H48C21.5 0 0 21.5 0 48v416c0 26.5 21.5 48 48 48h224c26.5 0 48-21.5 48-48V48c0-26.5-21.5-48-48-48zM160 480c-17.7 0-32-14.3-32-32s14.3-32 32-32 32 14.3 32 32-14.3 32-32 32zm112-108c0 6.6-5.4 12-12 12H60c-6.6 0-12-5.4-12-12V60c0-6.6 5.4-12 12-12h200c6.6 0 12 5.4 12 12v312z"></path></svg>
                `,
                command: 'set-device-mobile',
                togglable: false,
            }],
          }
        ]
      },
      
      // Disabling the storage manager, opting instead to inject
      // the content in manually rather than set up rest endpoints.
      storageManager: {
        type: null 
      },
      
      assetManager: {
        uploadFile: async e => {
          const files = e.dataTransfer ? e.dataTransfer.files : e.target.files;
          const urls = await uploadCompanyMedia(files);
          const assetManager = instance.AssetManager;
          for(const url of urls) {
            assetManager.add(url);
          }
        },
        assets: []
      },
      
      blockManager: {
        appendTo: '.blocks-container',
        blocks: [
          {
            id: 'section', // id is mandatory
            label: `
              <div>
                <div style="padding:9px 0">
                  <svg width="24" height="24" viewBox="0 0 512 512"><path fill="currentColor" d="M239.1 6.3l-208 78c-18.7 7-31.1 25-31.1 45v225.1c0 18.2 10.3 34.8 26.5 42.9l208 104c13.5 6.8 29.4 6.8 42.9 0l208-104c16.3-8.1 26.5-24.8 26.5-42.9V129.3c0-20-12.4-37.9-31.1-44.9l-208-78C262 2.2 250 2.2 239.1 6.3zM256 68.4l192 72v1.1l-192 78-192-78v-1.1l192-72zm32 356V275.5l160-65v133.9l-160 80z"></path></svg>
                </div>
                <div>
                  <b>Section</b>
                </div>
              </div>
            `,
            attributes: { class:'gjs-block-section' },
            content: `<section>
              <h1>This is a simple title</h1>
              <div>This is just a Lorem text: Lorem ipsum dolor sit amet</div>
            </section>`,
          }, {
            id: 'title',
            label: `
              <div>
                <div style="padding:9px 0">
                  <svg width="24" height="24" viewBox="0 0 512 512"><path fill="currentColor" d="M448 96v320h32a16 16 0 0 1 16 16v32a16 16 0 0 1-16 16H320a16 16 0 0 1-16-16v-32a16 16 0 0 1 16-16h32V288H160v128h32a16 16 0 0 1 16 16v32a16 16 0 0 1-16 16H32a16 16 0 0 1-16-16v-32a16 16 0 0 1 16-16h32V96H32a16 16 0 0 1-16-16V48a16 16 0 0 1 16-16h160a16 16 0 0 1 16 16v32a16 16 0 0 1-16 16h-32v128h192V96h-32a16 16 0 0 1-16-16V48a16 16 0 0 1 16-16h160a16 16 0 0 1 16 16v32a16 16 0 0 1-16 16z"></path></svg>
                </div>
                <div>
                  <b>Heading</b>
                </div>
              </div>
            `,
            content: '<h1 data-gjs-type="text">Insert heading here</h1>',
          }, {
            id: 'text',
            label: `
              <div>
                <div style="padding:9px 0">
                  <svg width="24" height="24" viewBox="0 0 448 512"><path fill="currentColor" d="M432 416h-23.41L277.88 53.69A32 32 0 0 0 247.58 32h-47.16a32 32 0 0 0-30.3 21.69L39.41 416H16a16 16 0 0 0-16 16v32a16 16 0 0 0 16 16h128a16 16 0 0 0 16-16v-32a16 16 0 0 0-16-16h-19.58l23.3-64h152.56l23.3 64H304a16 16 0 0 0-16 16v32a16 16 0 0 0 16 16h128a16 16 0 0 0 16-16v-32a16 16 0 0 0-16-16zM176.85 272L224 142.51 271.15 272z"></path></svg>
                </div>
                <div>
                  <b>Text</b>
                </div>
              </div>
            `,
            content: '<div data-gjs-type="text">Insert your text here</div>',
          }, {
            id: 'paragraph',
            label: `
              <div>
                <div style="padding:9px 0">
                  <svg width="24" height="24" viewBox="0 0 448 512"><path fill="currentColor" d="M12.83 352h262.34A12.82 12.82 0 0 0 288 339.17v-38.34A12.82 12.82 0 0 0 275.17 288H12.83A12.82 12.82 0 0 0 0 300.83v38.34A12.82 12.82 0 0 0 12.83 352zm0-256h262.34A12.82 12.82 0 0 0 288 83.17V44.83A12.82 12.82 0 0 0 275.17 32H12.83A12.82 12.82 0 0 0 0 44.83v38.34A12.82 12.82 0 0 0 12.83 96zM432 160H16a16 16 0 0 0-16 16v32a16 16 0 0 0 16 16h416a16 16 0 0 0 16-16v-32a16 16 0 0 0-16-16zm0 256H16a16 16 0 0 0-16 16v32a16 16 0 0 0 16 16h416a16 16 0 0 0 16-16v-32a16 16 0 0 0-16-16z"></path></svg>
                </div>
                <div>
                  <b>Paragraph</b>
                </div>
              </div>
            `,
            content: '<p data-gjs-type="text">Lorem ipsum dolor sit amet, consectetur adipiscing elit. Vestibulum sit amet dui justo. Donec molestie, nisl sed ultricies congue, turpis sem volutpat purus, id venenatis nunc diam luctus libero. Curabitur sed euismod libero, ut ultrices justo. Morbi ullamcorper hendrerit lacus, quis cursus mauris. Nullam posuere dignissim neque, sed aliquet mauris maximus quis. Etiam non lorem sit amet tellus semper iaculis ac vel mi. Vestibulum ante ipsum primis in faucibus orci luctus et ultrices posuere cubilia Curae; Duis pellentesque efficitur mauris sed ultricies. Ut ac mi pretium, pellentesque leo vel, bibendum augue. Nam pharetra libero elit, ut fringilla odio scelerisque non. Vestibulum eget laoreet dui. In commodo justo odio, ac ornare mi consectetur ac. Ut ipsum massa, faucibus eget magna non, mattis consequat metus. Sed nec eleifend lectus. Nullam luctus molestie dignissim.</p>',
          }, {
            id: 'image',
            label: `
              <div>
                <div style="padding:9px 0">
                  <svg width="24" height="24" viewBox="0 0 512 512"><path fill="currentColor" d="M464 448H48c-26.51 0-48-21.49-48-48V112c0-26.51 21.49-48 48-48h416c26.51 0 48 21.49 48 48v288c0 26.51-21.49 48-48 48zM112 120c-30.928 0-56 25.072-56 56s25.072 56 56 56 56-25.072 56-56-25.072-56-56-56zM64 384h384V272l-87.515-87.515c-4.686-4.686-12.284-4.686-16.971 0L208 320l-55.515-55.515c-4.686-4.686-12.284-4.686-16.971 0L64 336v48z"></path></svg>
                </div>
                <div>
                  <b>Image</b>
                </div>
              </div>
            `,
            // Select the component once it's dropped
            select: true,
            // You can pass components as a JSON instead of a simple HTML string,
            // in this case we also use a defined component type `image`
            content: { type: 'image' },
            // This triggers `active` event on dropped components and the `image`
            // reacts by opening the AssetManager
            activate: true,
          },
          {
            id: 'link', // id is mandatory
            label: `
              <div>
                <div style="padding:9px 0">
                  <svg width="24" height="24" viewBox="0 0 24 24"><path d="M15.31,8.69c2.8,2.8,2.76,7.3,0.02,10.06c-0.01,0.01-0.01,0.01-0.02,0.02l-3.15,3.15c-2.78,2.78-7.3,2.78-10.08,0 c-2.78-2.78-2.78-7.3,0-10.08l1.74-1.74c0.46-0.46,1.26-0.15,1.28,0.5c0.03,0.83,0.18,1.67,0.45,2.47c0.09,0.27,0.03,0.57-0.18,0.78 l-0.61,0.61c-1.31,1.31-1.35,3.45-0.05,4.78c1.31,1.34,3.47,1.35,4.8,0.02l3.15-3.15c1.32-1.32,1.32-3.46,0-4.77 c-0.17-0.17-0.35-0.31-0.48-0.4c-0.2-0.13-0.32-0.35-0.33-0.59c-0.02-0.5,0.16-1.01,0.55-1.4l0.99-0.99 c0.26-0.26,0.66-0.29,0.96-0.08C14.69,8.12,15.01,8.39,15.31,8.69L15.31,8.69z M21.92,2.08c-2.78-2.78-7.3-2.78-10.08,0L8.69,5.23 C8.68,5.24,8.68,5.25,8.67,5.25c-2.75,2.76-2.78,7.26,0.02,10.06c0.3,0.3,0.62,0.57,0.96,0.81c0.3,0.21,0.71,0.18,0.96-0.08 l0.99-0.99c0.39-0.39,0.57-0.9,0.55-1.4c-0.01-0.24-0.13-0.46-0.33-0.59c-0.14-0.09-0.31-0.23-0.48-0.4c-1.32-1.32-1.32-3.45,0-4.77 l3.15-3.15c1.32-1.32,3.48-1.32,4.8,0.02c1.3,1.33,1.26,3.47-0.05,4.78l-0.61,0.61c-0.2,0.2-0.27,0.51-0.18,0.78 c0.27,0.81,0.42,1.64,0.45,2.47c0.02,0.65,0.82,0.96,1.28,0.5l1.74-1.74C24.69,9.38,24.69,4.86,21.92,2.08L21.92,2.08z"/></svg>
                </div>
                <div>
                  <b>Link</b>
                </div>
              </div>
            `,
            attributes: { class:'gjs-block-section' },
            content: `<a>Link Text</a>`,
          }
        ]
      },
      
      layerManager: {
        appendTo: '.layers-container'
      },
      
      traitManager: {
        appendTo: '.traits-container',
      },
      
      deviceManager: {
        devices: [{
            name: 'Desktop',
            width: '', // default size
          }, {
            name: 'Mobile',
            width: '320px', // this value will be used on canvas width
            widthMedia: '480px', // this value will be used in CSS @media
        }]
      },
      
      // The Selector Manager allows to assign classes and
      // different states (eg. :hover) on components.
      // Generally, it's used in conjunction with Style Manager
      // but it's not mandatory
      // selectorManager: {
      //   appendTo: '.styles-container'
      // },
      
      styleManager: {
        appendTo: '.styles-container',
        sectors: [
          {
            name: "Layout",
            open: false,
            buildProps: [
              "display",
              "flex-direction",
              "justify-content",
              "align-items",
              "align-content",
              "order",
              "flex-basis",
              "flex-grow",
              "flex-shrink",
              "flex-wrap",
            ]
          },
          {
            name: 'Dimensions',
            open: false,
            // Use built-in properties
            buildProps: [
              "width",
              "height",
              "min-width",
              "min-height",
              "max-width",
              "max-height",  
              "padding",
              "margin",
            ],
            // Use `properties` to define/override single property
            // properties: [
            //   {
            //     // Type of the input,
            //     // options: integer | radio | select | color | slider | file | composite | stack
            //     type: 'integer',
            //     name: 'The width', // Label for the property
            //     property: 'width', // CSS property (if buildProps contains it will be extended)
            //     units: ['px', '%'], // Units, available only for 'integer' types
            //     defaults: 'auto', // Default value
            //     min: 0, // Min value, available only for 'integer' types
            //   }
            // ]
          },{
            name: 'Typography',
            open: false,
            buildProps: [
              "color",
              "font-family",
              "font-size",
              "font-weight",
              "line-height",
              "letter-spacing",
              "text-align",
              "text-shadow",
            ]
          },{
            name: 'Position',
            open: false,
            buildProps: [
              "position",
              "top",
              "right",
              "bottom",
              "left",
            ]
          },{
            name: 'Background',
            open: false,
            buildProps: [
              "background-image",
              "background-position",
              "background-size",
              "background-color",
            ]
          },{
            name: 'Border',
            open: false,
            buildProps: [
              "border",
              "border-radius",
            ]
          },{
            name: 'Shadow',
            open: false,
            buildProps: [
              "box-shadow",
            ]
          },{
            name: 'Cursor',
            open: false,
            buildProps: [
              "cursor",
            ]
          }
        ]
      },
    });
    
    
    // Device Commands
    
    instance.Commands.add('set-device-desktop', {
      run: inst => inst.setDevice('Desktop')
    });
    instance.Commands.add('set-device-mobile', {
      run: inst => inst.setDevice('Mobile')
    });
    
    
    // Load company media and add it to the editor.
    
    loadMedia().then(urls => {
      const assetManager = instance.AssetManager;
      for(const url of urls) {
        assetManager.add(url);
      }
    });
    
    
    // Set the editor so it's accessible in state.
    
    setEditor(instance);
    
  }, [page]); // eslint-disable-line 
  
  
  
  // Save the page and redirect to the grid.
  
  async function save() {
    if(!editor) {
      return;
    }
    await updatePage(companyId, pageId, {
      html: editor.getHtml(),
      css: editor.getCss(),
    });
    
    refreshPagePreview(companyId, pageId);
    
    if(page.campaignId !== "NONE") {
      history.push(`/campaigns/${page.campaignId}`);
    }
    else {
      history.push('/pages');
    }
  }
  
  
  // Store the active panel, showing that panel and
  // hiding inavtive panes. 
  
  const [panel, setPanel] = useState("BLOCKS");
  
  // Layout
  
  if(!page) {
    return null;  
  }
  
  return (
    <div id="pageedit">
      <div id="pageedit-left">

        
        <div id="pageedit-tools" className="gjs-one-bg">
          <div id="pageedit-actions"></div>
          <div id="pageedit-devices"></div>
        </div>
        
        <div id="pageedit-canvas">
          <div id="gjs" key="page-gjs"/>
        </div>
        
        
      </div>
      <div id="pageedit-right">
      
      
        <div id="pageedit-switcher">
          <div 
            className={`switcher-button ${panel==="BLOCKS" ? 'active' : ''}`}
            onClick={() => setPanel("BLOCKS")}>
            <FontAwesomeIcon icon={faCubes} />
          </div>
          <div 
            className={`switcher-button ${panel==="STYLES" ? 'active' : ''}`}
            onClick={() => setPanel("STYLES")}>
            <FontAwesomeIcon icon={faPaintBrush} />
          </div>
          <div 
            className={`switcher-button ${panel==="LAYERS" ? 'active' : ''}`}
            onClick={() => setPanel("LAYERS")}>
            <FontAwesomeIcon icon={faLayerGroup} />
          </div>
          <div 
            className={`switcher-button ${panel==="TRAITS" ? 'active' : ''}`}
            onClick={() => setPanel("TRAITS")}>
            <FontAwesomeIcon icon={faCog} />
          </div>
        </div>
        
        <div id="pageedit-panels">
          <div className="blocks-container" style={{display:panel==="BLOCKS" ? '' : 'none'}}></div>
          <div className="layers-container" style={{display:panel==="LAYERS" ? '' : 'none'}}></div>
          <div className="styles-container" style={{display:panel==="STYLES" ? '' : 'none'}}></div>
          <div className="traits-container" style={{display:panel==="TRAITS" ? '' : 'none'}}></div>
        </div>
        
        <div id="pageedit-file">
          <Button label="SAVE & CLOSE" small white onClick={save}/>
        </div>
        
        
      </div>
    </div>
  );
}


